/**
 * CRYELA IMAGE ZOOM — assets/cryela-zoom.js
 *
 * Opens a full-screen lightbox on any product image.
 *
 * Desktop
 *   • Click image or zoom button → open
 *   • Scroll wheel → zoom toward cursor
 *   • Mouse drag → pan (only when zoom > 1)
 *   • +/- keys, click buttons → zoom step
 *   • Arrow keys → navigate images
 *   • ESC → close
 *
 * Mobile
 *   • Tap image or zoom button → open
 *   • Pinch → zoom
 *   • Single-finger drag → pan (when zoom > 1)
 *   • Single-finger swipe → navigate images (when zoom = 1)
 *   • Does NOT interfere with page scroll (body locked only when modal is open)
 */

(function () {
  'use strict';

  /* ─────────────────────── CONFIG ───────────────────────── */
  var CFG = {
    minZoom:        1,
    maxZoom:        4,
    zoomStep:       0.35,
    swipeMinPx:     48,   // min horizontal px to count as a swipe
    swipeMaxAngle:  38    // max degrees from horizontal for a swipe
  };

  /* ─────────────────────── STATE ────────────────────────── */
  var overlay   = null;
  var elImg     = null;
  var elStage   = null;
  var elLevel   = null;
  var elCounter = null;
  var elPrev    = null;
  var elNext    = null;

  var images    = [];   // [{ src, alt }, …]
  var activeIdx = 0;

  var zoom   = 1;
  var panX   = 0;
  var panY   = 0;

  /* mouse drag */
  var isDragging  = false;
  var dragAnchor  = { x: 0, y: 0, px: 0, py: 0 };

  /* touch */
  var touchAnchor    = { x: 0, y: 0, px: 0, py: 0 };
  var pinchBase      = { dist: 0, zoom: 1 };
  var swipeStart     = { x: 0, y: 0 };
  var touchMoved     = false;
  var activeTouches  = 0;

  /* ─────────────────────── BUILD OVERLAY ────────────────── */
  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.id = 'czoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Zoom immagine');

    overlay.innerHTML =
      '<div class="czoom-bg"></div>' +
      '<div class="czoom-wrap">' +

        /* Close */
        '<button class="czoom-x" aria-label="Chiudi (ESC)">' +
          icon('M18 6 6 18M6 6l12 12') +
        '</button>' +

        /* Prev */
        '<button class="czoom-arr czoom-arr--l" aria-label="Immagine precedente">' +
          icon('M15 18l-6-6 6-6') +
        '</button>' +

        /* Stage */
        '<div class="czoom-stage">' +
          '<img class="czoom-img" src="" alt="" draggable="false">' +
        '</div>' +

        /* Next */
        '<button class="czoom-arr czoom-arr--r" aria-label="Immagine successiva">' +
          icon('M9 18l6-6-6-6') +
        '</button>' +

        /* Bottom bar */
        '<div class="czoom-bar">' +
          '<button class="czoom-z czoom-z--out" aria-label="Riduci zoom (−)">' +
            icon('M5 12h14') +
          '</button>' +
          '<span class="czoom-pct">100%</span>' +
          '<button class="czoom-z czoom-z--in" aria-label="Aumenta zoom (+)">' +
            icon('M12 5v14M5 12h14') +
          '</button>' +
          '<button class="czoom-z czoom-z--rst" aria-label="Reimposta zoom (0)">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>' +
            '<path d="M3 3v5h5"/></svg>' +
          '</button>' +
          '<span class="czoom-sep"></span>' +
          '<span class="czoom-cnt"></span>' +
          '<span class="czoom-hint">Rotella per zoom • Trascina per muovere</span>' +
        '</div>' +

      '</div>';

    document.body.appendChild(overlay);

    /* Cache elements */
    elImg     = overlay.querySelector('.czoom-img');
    elStage   = overlay.querySelector('.czoom-stage');
    elLevel   = overlay.querySelector('.czoom-pct');
    elCounter = overlay.querySelector('.czoom-cnt');
    elPrev    = overlay.querySelector('.czoom-arr--l');
    elNext    = overlay.querySelector('.czoom-arr--r');

    /* ── Event listeners ── */
    overlay.querySelector('.czoom-bg').addEventListener('click', closeZoom);
    overlay.querySelector('.czoom-x').addEventListener('click', closeZoom);
    elPrev.addEventListener('click', function () { navigate(-1); });
    elNext.addEventListener('click', function () { navigate(+1); });
    overlay.querySelector('.czoom-z--in').addEventListener('click', function () {
      setZoom(zoom + CFG.zoomStep, null, null, true);
    });
    overlay.querySelector('.czoom-z--out').addEventListener('click', function () {
      setZoom(zoom - CFG.zoomStep, null, null, true);
    });
    overlay.querySelector('.czoom-z--rst').addEventListener('click', resetView);

    /* Wheel zoom */
    elStage.addEventListener('wheel', onWheel, { passive: false });

    /* Mouse drag */
    elStage.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);

    /* Touch */
    elStage.addEventListener('touchstart', onTouchStart, { passive: true });
    elStage.addEventListener('touchmove',  onTouchMove,  { passive: false });
    elStage.addEventListener('touchend',   onTouchEnd,   { passive: true });
    elStage.addEventListener('touchcancel',onTouchEnd,   { passive: true });

    /* Keyboard */
    document.addEventListener('keydown', onKey);
  }

  function icon(d) {
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
      ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="' + d + '"/></svg>'
    );
  }

  /* ─────────────────────── OPEN / CLOSE ─────────────────── */
  function openZoom(idx) {
    if (!overlay) buildOverlay();
    activeIdx = wrap(idx, images.length);
    loadImage(activeIdx);
    resetView();
    overlay.classList.add('is-open');
    document.documentElement.classList.add('czoom-lock');
    /* Delay focus so screen readers don't announce the image twice */
    setTimeout(function () {
      var btn = overlay.querySelector('.czoom-x');
      if (btn) btn.focus();
    }, 80);
  }

  function closeZoom() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('czoom-lock');
  }

  /* ─────────────────────── NAVIGATION ───────────────────── */
  function navigate(dir) {
    if (images.length < 2) return;
    activeIdx = wrap(activeIdx + dir, images.length);
    loadImage(activeIdx);
    resetView();
  }

  function loadImage(idx) {
    if (!elImg || !images[idx]) return;
    elImg.src = images[idx].src;
    elImg.alt = images[idx].alt || '';
    syncUI();
  }

  /* ─────────────────────── ZOOM & PAN ───────────────────── */
  /**
   * @param {number}  z        - target zoom level
   * @param {number|null} ox   - pointer X in viewport coords (zoom origin)
   * @param {number|null} oy   - pointer Y in viewport coords
   * @param {boolean} animate  - add transition class
   */
  function setZoom(z, ox, oy, animate) {
    var prev = zoom;
    zoom = clamp(z, CFG.minZoom, CFG.maxZoom);

    if (zoom === prev) return;
    var ratio = zoom / prev;

    if (ox != null && oy != null) {
      /* zoom toward pointer */
      var r  = elStage.getBoundingClientRect();
      var cx = ox - r.left - r.width  / 2;
      var cy = oy - r.top  - r.height / 2;
      panX   = (panX - cx) * ratio + cx;
      panY   = (panY - cy) * ratio + cy;
    } else {
      /* zoom toward center */
      panX *= ratio;
      panY *= ratio;
    }

    if (zoom <= CFG.minZoom) { panX = 0; panY = 0; }
    clampPan();
    applyTransform(animate);
    syncUI();
  }

  function resetView() {
    zoom = 1; panX = 0; panY = 0;
    applyTransform(true);
    syncUI();
  }

  function clampPan() {
    if (zoom <= CFG.minZoom || !elImg || !elStage) {
      panX = 0; panY = 0; return;
    }
    var sw = elStage.clientWidth;
    var sh = elStage.clientHeight;
    var iw = elImg.naturalWidth  || elImg.clientWidth  || 1;
    var ih = elImg.naturalHeight || elImg.clientHeight || 1;
    var sc = Math.min(sw / iw, sh / ih);
    var maxX = Math.max(0, (iw * sc * zoom - sw) / 2);
    var maxY = Math.max(0, (ih * sc * zoom - sh) / 2);
    panX = clamp(panX, -maxX, maxX);
    panY = clamp(panY, -maxY, maxY);
  }

  function applyTransform(animate) {
    if (!elImg) return;

    /* Toggle transition class */
    if (animate) {
      elImg.classList.add('is-animating');
      elImg.classList.remove('is-dragging');
    } else {
      elImg.classList.remove('is-animating');
      elImg.classList.add('is-dragging');
    }

    elImg.style.transform =
      'translate(' + panX + 'px,' + panY + 'px) scale(' + zoom + ')';

    /* Cursor feedback */
    if (elStage) {
      elStage.style.cursor =
        zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in';
    }
  }

  /* ─────────────────────── MOUSE EVENTS ─────────────────── */
  function onWheel(e) {
    e.preventDefault();
    var delta = e.deltaY < 0 ? CFG.zoomStep : -CFG.zoomStep;
    /* Normalise trackpad events */
    if (Math.abs(e.deltaY) < 50) delta *= 0.5;
    setZoom(zoom + delta, e.clientX, e.clientY, false);
    /* Ensure last frame is smooth */
    clearTimeout(onWheel._t);
    onWheel._t = setTimeout(function () { applyTransform(true); }, 80);
  }

  function onMouseDown(e) {
    if (zoom <= 1 || e.button !== 0) return;
    isDragging = true;
    dragAnchor = { x: e.clientX, y: e.clientY, px: panX, py: panY };
    applyTransform(false);
    e.preventDefault();
  }

  function onMouseMove(e) {
    if (!isDragging) return;
    panX = dragAnchor.px + (e.clientX - dragAnchor.x);
    panY = dragAnchor.py + (e.clientY - dragAnchor.y);
    clampPan();
    applyTransform(false);
  }

  function onMouseUp() {
    if (!isDragging) return;
    isDragging = false;
    applyTransform(true);
  }

  /* ─────────────────────── TOUCH EVENTS ─────────────────── */
  function onTouchStart(e) {
    activeTouches = e.touches.length;
    touchMoved    = false;

    if (e.touches.length === 1) {
      var t        = e.touches[0];
      swipeStart   = { x: t.clientX, y: t.clientY };
      touchAnchor  = { x: t.clientX, y: t.clientY, px: panX, py: panY };

    } else if (e.touches.length === 2) {
      pinchBase = {
        dist: ptDist(e.touches[0], e.touches[1]),
        zoom: zoom
      };
    }
  }

  function onTouchMove(e) {
    touchMoved    = true;
    activeTouches = e.touches.length;

    if (e.touches.length === 2) {
      /* ── Pinch to zoom ── */
      e.preventDefault();
      var d     = ptDist(e.touches[0], e.touches[1]);
      var ratio = d / pinchBase.dist;
      setZoom(pinchBase.zoom * ratio, null, null, false);

    } else if (e.touches.length === 1 && zoom > CFG.minZoom) {
      /* ── Single-finger pan (only when zoomed in) ── */
      e.preventDefault();
      var t = e.touches[0];
      var dx = t.clientX - touchAnchor.x;
      var dy = t.clientY - touchAnchor.y;

      panX = touchAnchor.px + dx;
      panY = touchAnchor.py + dy;
      clampPan();
      applyTransform(false);

      /* Incremental anchor so transition from pinch → drag is seamless */
      touchAnchor.x  = t.clientX;
      touchAnchor.y  = t.clientY;
      touchAnchor.px = panX;
      touchAnchor.py = panY;

    }
    /* zoom = 1 + 1 finger: do NOT preventDefault() — body is locked
       by czoom-lock so the page won't actually scroll, but we still
       detect the swipe direction in onTouchEnd                       */
  }

  function onTouchEnd(e) {
    /* Ignore if other fingers are still active */
    if (e.touches.length > 0) return;

    /* After pinch ends, refresh anchor for potential pan continuation */
    if (activeTouches >= 2) {
      activeTouches = 0; return;
    }
    activeTouches = 0;

    /* ── Swipe to navigate (zoom = 1, significant horizontal move) ── */
    if (touchMoved && zoom <= CFG.minZoom && e.changedTouches.length) {
      var ct = e.changedTouches[0];
      var dx = ct.clientX - swipeStart.x;
      var dy = ct.clientY - swipeStart.y;
      var angle = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);
      var isHoriz = angle < CFG.swipeMaxAngle || angle > 180 - CFG.swipeMaxAngle;

      if (isHoriz && Math.abs(dx) >= CFG.swipeMinPx) {
        navigate(dx < 0 ? +1 : -1);
      }
    }
  }

  /* ─────────────────────── KEYBOARD ─────────────────────── */
  function onKey(e) {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    switch (e.key) {
      case 'Escape':     closeZoom(); break;
      case 'ArrowLeft':  navigate(-1); break;
      case 'ArrowRight': navigate(+1); break;
      case '+':
      case '=':          setZoom(zoom + CFG.zoomStep, null, null, true); break;
      case '-':          setZoom(zoom - CFG.zoomStep, null, null, true); break;
      case '0':          resetView(); break;
      default: return;
    }
    e.preventDefault();
  }

  /* ─────────────────────── UI SYNC ───────────────────────── */
  function syncUI() {
    if (elLevel)   elLevel.textContent   = Math.round(zoom * 100) + '%';
    if (elCounter) elCounter.textContent =
      images.length > 1 ? (activeIdx + 1) + ' / ' + images.length : '';

    var multi = images.length > 1;
    if (elPrev) elPrev.style.visibility = multi ? 'visible' : 'hidden';
    if (elNext) elNext.style.visibility = multi ? 'visible' : 'hidden';
  }

  /* ─────────────────────── HELPERS ───────────────────────── */
  function ptDist(a, b) {
    var dx = a.clientX - b.clientX;
    var dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function wrap(i, len) { return ((i % len) + len) % len; }

  /**
   * Pick the highest-resolution URL from a srcset attribute,
   * falling back to src.
   */
  function bestSrc(img) {
    var ss = img.getAttribute('srcset');
    if (ss) {
      var parts = ss.split(',');
      /* The last entry is the widest — "url 1200w" */
      var last = parts[parts.length - 1].trim().split(/\s+/);
      if (last[0]) return last[0];
    }
    return img.src;
  }

  /* ─────────────────────── INIT ──────────────────────────── */
  function init() {
    document.querySelectorAll('.cryela-product').forEach(function (section) {
      var frames = Array.prototype.slice.call(
        section.querySelectorAll('[data-cryela-frame]')
      );
      if (!frames.length) return;

      /* Build the shared image list */
      images = frames.map(function (frame) {
        var img = frame.querySelector('.cryela-product__img');
        return img
          ? { src: bestSrc(img), alt: img.alt || '' }
          : { src: '', alt: '' };
      });

      /* Attach zoom button + click handler to every frame */
      frames.forEach(function (frame, idx) {

        /* Zoom icon button — appears bottom-right of the image */
        var btn       = document.createElement('button');
        btn.className = 'czoom-trigger';
        btn.type      = 'button';
        btn.setAttribute('aria-label', 'Ingrandisci immagine');
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
          ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<circle cx="11" cy="11" r="8"/>' +
          '<path d="m21 21-4.35-4.35"/>' +
          '<path d="M11 8v6M8 11h6"/>' +
          '</svg>';
        frame.appendChild(btn);

        /* Refresh image source at open time (src might update after init) */
        function doOpen() {
          var img = frame.querySelector('.cryela-product__img');
          if (img) images[idx] = { src: bestSrc(img), alt: img.alt || '' };
          openZoom(idx);
        }

        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          doOpen();
        });

        /* Also open on image click */
        var img = frame.querySelector('.cryela-product__img');
        if (img) img.addEventListener('click', doOpen);
      });
    });
  }

  /* ─────────────────────── BOOTSTRAP ─────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
